import { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import NotificationEventCard from './NotificationEventCard';

/**
 * Module-specific notification config.
 *
 * Dual-mode component:
 *  - If the module has rows in `notification_events` (e.g. dispatch),
 *    render the config-driven event card list (new system).
 *  - Otherwise, fall back to the existing subscription-based UI.
 *
 * Modules will migrate one at a time; both code paths coexist during that
 * transition.
 */

// Fallback for legacy events without an explicit `parent_module`. New events
// are auto-generated from workflow_templates server-side and carry their own
// parent_module, so this map only catches static/legacy rows.
const LEGACY_PARENT_FALLBACK = {
  dispatch: 'dispatch',
  expenses: 'sales',
  refunds: 'sales',
  sales: 'sales',
  maintenance: 'maintenance',
  leaderboard: 'leaderboard',
  compliance: 'admin',
  admin: 'admin',
  tasks: 'tasks',
};

export default function ModuleNotificationsTab({ moduleKey, scope, scopeLoading }) {
  const [events, setEvents] = useState(null);         // null = not yet loaded
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const loadEvents = useCallback(async () => {
    try {
      const data = await api.getModuleNotificationEvents(moduleKey);
      setEvents(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load module notification events:', err);
      setEvents([]);
    }
  }, [moduleKey]);

  const load = useCallback(async () => {
    try {
      const [legacy] = await Promise.all([
        api.getNotificationConfig().catch(() => null),
        loadEvents(),
      ]);
      setConfig(legacy);
    } catch (err) {
      console.error('Failed to load notifications:', err);
    }
    setLoading(false);
  }, [loadEvents]);

  useEffect(() => { load(); }, [load]);

  if (loading || scopeLoading) return <p>Loading notification settings...</p>;

  // NEW: config-driven event cards (dispatch today, more modules later)
  if (Array.isArray(events) && events.length > 0) {
    return (
      <div>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: 16 }}>
          Each event below fires automatically when something happens in this module.
          Edit a filter to change <em>who</em> gets notified. Super admins can disable
          an event entirely.
        </p>
        {events.map(ev => (
          <NotificationEventCard
            key={ev.id}
            event={ev}
            moduleKey={moduleKey}
            onChanged={loadEvents}
          />
        ))}
      </div>
    );
  }

  // FALLBACK: legacy subscription UI (expenses, refunds, maintenance, etc.)
  if (!config) return <p>Failed to load notification config.</p>;

  const { events: legacyEvents, subscriptions, users } = config;

  // Restrict recipients to users in the module's Access scope.
  // Still require a phone number (can't SMS without one).
  const scopeUserIds = new Set((scope?.users || []).map(u => u.id));
  const hasScope = scopeUserIds.size > 0;
  const eligibleUsers = users
    .filter(u => u.phone && u.phone.trim() !== '')
    .filter(u => scopeUserIds.has(u.id));

  // Show any sub-module whose events claim `parent_module === moduleKey`.
  // Falls back to the legacy map for events that haven't tagged themselves
  // (keeps prior behavior for any old rows during rollout).
  const filteredModuleKeys = Object.keys(legacyEvents).filter(k => {
    const eventsForKey = legacyEvents[k] || [];
    // If ANY event on this sub-module is tagged with this parent, include it.
    if (eventsForKey.some(e => e.parent_module === moduleKey)) return true;
    // Legacy: fall back to the static map when parent_module isn't set.
    if (eventsForKey.every(e => !e.parent_module)) {
      return (LEGACY_PARENT_FALLBACK[k] || k) === moduleKey;
    }
    return false;
  });

  if (!hasScope) {
    return (
      <div style={{
        padding: 20, borderRadius: 8,
        background: 'rgba(218, 165, 32, 0.08)',
        border: '1px solid rgba(218, 165, 32, 0.25)',
        color: 'var(--text-primary)',
      }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Configure Access first</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          No users have been granted access to this module yet. Open the <strong>Access</strong> tab
          to pick a team, position, or user. Only people in that scope can receive notifications here.
        </div>
      </div>
    );
  }

  if (filteredModuleKeys.length === 0) {
    return <p style={{ color: 'var(--text-secondary)' }}>No notification events configured for this module yet.</p>;
  }

  function isSubscribed(mk, eventType, userId) {
    return subscriptions.some(s => s.module_key === mk && s.event_type === eventType && s.user_id === userId);
  }

  function getThreshold(mk, eventType) {
    const sub = subscriptions.find(s => s.module_key === mk && s.event_type === eventType && s.threshold_days);
    return sub?.threshold_days || null;
  }

  async function toggleSubscription(mk, eventType, userId, eventDef) {
    const key = `${mk}.${eventType}.${userId}`;
    setSaving(key);
    try {
      if (isSubscribed(mk, eventType, userId)) {
        await api.unsubscribeNotification({ module_key: mk, event_type: eventType, user_id: userId });
      } else {
        const payload = { module_key: mk, event_type: eventType, user_id: userId };
        if (eventDef.has_threshold) {
          payload.threshold_days = getThreshold(mk, eventType) || eventDef.default_threshold;
        }
        await api.subscribeNotification(payload);
      }
      await load();
    } catch (err) {
      console.error('Notification toggle failed:', err);
    }
    setSaving(null);
  }

  async function handleThresholdChange(mk, eventType, value) {
    const days = parseInt(value);
    if (!days || days < 1) return;
    setSaving(`threshold.${mk}.${eventType}`);
    try {
      await api.updateNotificationThreshold({ module_key: mk, event_type: eventType, threshold_days: days });
      await load();
    } catch (err) {
      console.error('Threshold update failed:', err);
    }
    setSaving(null);
  }

  return (
    <div>
      <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: 16 }}>
        Configure which users receive SMS alerts for events in this module.
        Only users in the module's Access scope with phone numbers are shown.
      </p>

      {eligibleUsers.length === 0 && (
        <div className="card" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
          No users in this module's access scope have a phone number on file.
        </div>
      )}

      {filteredModuleKeys.map(mk => {
        const moduleEvents = legacyEvents[mk];
        return (
          <div key={mk} className="card" style={{ marginBottom: '1.5rem', padding: 0, overflow: 'hidden' }}>
            {filteredModuleKeys.length > 1 && (
              <div style={{
                padding: '0.75rem 1.5rem',
                background: 'var(--surface-raised)',
                borderBottom: '1px solid var(--border-subtle)',
                fontWeight: 600,
                fontSize: '0.9rem',
              }}>
                {mk.charAt(0).toUpperCase() + mk.slice(1)}
              </div>
            )}

            <div className="table-wrapper">
              <table className="data-table" style={{ margin: 0, borderRadius: 0 }}>
                <thead>
                  <tr>
                    <th style={{ minWidth: 200 }}>Event</th>
                    {eligibleUsers.map(u => (
                      <th key={u.id} style={{ textAlign: 'center', minWidth: 100, fontSize: '0.8rem' }}>
                        <div>{u.full_name}</div>
                      </th>
                    ))}
                    <th style={{ minWidth: 80, textAlign: 'center' }}>Threshold</th>
                  </tr>
                </thead>
                <tbody>
                  {moduleEvents.map(evt => {
                    const threshold = getThreshold(mk, evt.event);
                    return (
                      <tr key={evt.event}>
                        <td>
                          <span style={{ fontWeight: 500 }}>{evt.label}</span>
                          {evt.has_threshold && (
                            <span style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', marginLeft: 8 }}>(daily alert)</span>
                          )}
                        </td>
                        {eligibleUsers.map(u => {
                          const subbed = isSubscribed(mk, evt.event, u.id);
                          const key = `${mk}.${evt.event}.${u.id}`;
                          const isSaving = saving === key;
                          return (
                            <td key={u.id} style={{ textAlign: 'center', padding: '0.5rem' }}>
                              <input
                                type="checkbox"
                                checked={subbed}
                                disabled={isSaving}
                                onChange={() => toggleSubscription(mk, evt.event, u.id, evt)}
                                style={{ width: 18, height: 18, cursor: isSaving ? 'wait' : 'pointer', accentColor: 'var(--brand-gold)' }}
                              />
                            </td>
                          );
                        })}
                        <td style={{ textAlign: 'center' }}>
                          {evt.has_threshold ? (
                            <input
                              type="number"
                              min="1"
                              value={threshold || evt.default_threshold || ''}
                              onChange={e => handleThresholdChange(mk, evt.event, e.target.value)}
                              disabled={saving?.startsWith(`threshold.${mk}.${evt.event}`)}
                              style={{ width: 50, textAlign: 'center', fontSize: '0.85rem' }}
                            />
                          ) : (
                            <span style={{ color: 'var(--text-secondary)' }}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}
